/**
 * useMfaStepUp — provider + hook per richiedere step-up MFA.
 *
 * Pattern di uso:
 *   const { requireTier1, requireTier2 } = useMfaStepUp();
 *   await requireTier2('Esporta dati paziente');
 *   // poi esegui l'azione
 *
 * Internamente:
 * - Legge il tier corrente da useAuth().
 * - Se sufficiente, risolve immediatamente.
 * - Altrimenti apre MfaModal e attende il successo (resolve) o il
 *   cancel (reject).
 *
 * TRE LIVELLI, NON DUE (PR-G4a):
 *   requireTier1    12h    letture staff, scritture template
 *   requireClinical  4h    scritture su dati clinici art. 9
 *   requireTier2     5min  operazioni critiche
 *
 * `requireClinical` non e' un tier: 4h cade dentro il Tier 1, quindi non
 * e' esprimibile con `mfaTier >= n`. Si confronta direttamente
 * `auth_time` con la soglia, e quando e' scaduta si riusa il flusso
 * Tier 2 — che riporta `auth_time` a ora e soddisfa la finestra clinica
 * per costruzione. Il modale mostra la formulazione Tier 2: chiedere
 * l'OTP per scrivere su un fascicolo sanitario e' un'azione critica.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { useAuth } from './useAuth';
import type { MfaTier } from '@shared/types/auth';
import { isClinicalMfaFresh } from '../security/mfa-freshness';
import { MfaModal } from '../components/auth/MfaModal';

type Tier = 'tier1' | 'tier2';

interface PendingRequest {
  tier: Tier;
  actionLabel: string;
  resolve: () => void;
  reject: (reason: unknown) => void;
}

interface MfaStepUpApi {
  requireTier1: (actionLabel: string) => Promise<void>;
  requireTier2: (actionLabel: string) => Promise<void>;
  /**
   * Finestra clinica di 4h (`assertMfaFresh4h` lato Cloud Function).
   * Da invocare PRIMA della callable, non dopo il `failed-precondition`.
   */
  requireClinical: (actionLabel: string) => Promise<void>;
}

const MfaStepUpContext = createContext<MfaStepUpApi | null>(null);

export function MfaStepUpProvider({ children }: { children: React.ReactNode }) {
  const { mfaTier, authTimeSec, refreshTokenAndAssert } = useAuth();
  const [pending, setPending] = useState<PendingRequest | null>(null);
  const tierRef = useRef<MfaTier>(mfaTier);
  // "Latest ref" pattern: tierRef segue il valore corrente di mfaTier
  // così la closure dentro `requireTier` legge sempre l'ultimo tier
  // senza dover essere ricreata.
  // eslint-disable-next-line react-hooks/refs
  tierRef.current = mfaTier;
  const authTimeRef = useRef<number | null>(authTimeSec);
  // Stesso pattern per auth_time: la finestra clinica si misura sul
  // valore grezzo, non sul tier.
  // eslint-disable-next-line react-hooks/refs
  authTimeRef.current = authTimeSec;

  const requireTier = useCallback(
    (tier: Tier, actionLabel: string) => {
      const required: MfaTier = tier === 'tier2' ? 2 : 1;
      if (tierRef.current >= required) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve, reject) => {
        setPending({ tier, actionLabel, resolve, reject });
      });
    },
    []
  );

  const requireClinical = useCallback((actionLabel: string) => {
    if (isClinicalMfaFresh(authTimeRef.current)) {
      return Promise.resolve();
    }
    // Oltre le 4h il tier corrente e' 1 o 0, mai 2: il modale si apre
    // sempre, e al successo `auth_time` torna a ora.
    return new Promise<void>((resolve, reject) => {
      setPending({ tier: 'tier2', actionLabel, resolve, reject });
    });
  }, []);

  const handleSuccess = useCallback(async () => {
    if (!pending) return;
    try {
      // MfaModal ha gia' invocato refreshTokenAndAssert internamente al
      // termine del suo step OTP. Lo invochiamo di nuovo qui come
      // garanzia (idempotente): se per qualunque motivo il singleton
      // non avesse ancora visto l'aggiornamento, lo forziamo prima di
      // risolvere la promise verso il caller di `requireTier2`.
      await refreshTokenAndAssert();
      pending.resolve();
    } catch (err) {
      pending.reject(err);
    } finally {
      setPending(null);
    }
  }, [pending, refreshTokenAndAssert]);

  const handleCancel = useCallback(() => {
    if (!pending) return;
    toast.error(`Operazione "${pending.actionLabel}" annullata.`);
    pending.reject(new Error('mfa-cancelled'));
    setPending(null);
  }, [pending]);

  const api = useMemo<MfaStepUpApi>(
    () => ({
      requireTier1: (label) => requireTier('tier1', label),
      requireTier2: (label) => requireTier('tier2', label),
      requireClinical,
    }),
    [requireTier, requireClinical]
  );

  return (
    <MfaStepUpContext.Provider value={api}>
      {children}
      {pending && (
        <MfaModal
          isOpen
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          tier={pending.tier}
          actionLabel={pending.actionLabel}
        />
      )}
    </MfaStepUpContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMfaStepUp(): MfaStepUpApi {
  const ctx = useContext(MfaStepUpContext);
  if (!ctx) {
    throw new Error('useMfaStepUp deve essere usato dentro <MfaStepUpProvider>');
  }
  return ctx;
}
